import React, { useState, useEffect } from 'react';
import type { CompanyAccountInfo } from '../types';

interface CompanyAccountInfoCardProps {
  accountInfo: CompanyAccountInfo;
  isEditing: boolean;
  onEdit: () => void;
  onSave: (newEmail: string, password: string) => Promise<void>;
  onCancel: () => void;
  loading: boolean;
}

const CompanyAccountInfoCard: React.FC<CompanyAccountInfoCardProps> = ({
  accountInfo,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  loading,
}) => {
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setNewEmail('');
    setPassword('');
    setError('');
  }, [accountInfo, isEditing]);

  const handleSave = async () => {
    setError('');

    // Validate email
    if (!newEmail) {
      setError('New email is required');
      return;
    }
    if (newEmail === accountInfo.primaryContactEmail) {
      setError('New email must be different from current email');
      return;
    }
    if (!password) {
      setError('Password is required for security verification');
      return;
    }

    try {
      await onSave(newEmail, password);
    } catch (err: any) {
      setError(err.message || 'Failed to update email');
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 border border-[#EDEDED]">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2
            style={{
              fontFamily: 'Manrope',
              fontWeight: 600,
              fontSize: '20px',
              lineHeight: '27px',
              color: '#0B111E',
            }}
          >
            Account Information
          </h2>
          <p
            style={{
              fontFamily: 'Manrope',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '19px',
              color: '#757575',
              marginTop: '4px',
            }}
          >
            Manage your account email and owner details
          </p>
        </div>
        {!isEditing && (
          <button
            onClick={onEdit}
            className="px-4 py-2 text-[#015FA6] hover:bg-blue-50 rounded-lg transition-colors"
            style={{ fontFamily: 'Manrope', fontWeight: 500, fontSize: '14px' }}
          >
            Edit
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          {error && (
            <div
              className="p-3 bg-red-50 border border-red-200 rounded-lg"
              style={{ fontFamily: 'Manrope', fontSize: '14px', color: '#DC2626' }}
            >
              {error}
            </div>
          )}
          <div>
            <label
              style={{
                fontFamily: 'Manrope',
                fontWeight: 500,
                fontSize: '14px',
                color: '#0B111E',
              }}
            >
              Current Email
            </label>
            <input
              type="email"
              value={accountInfo.primaryContactEmail}
              disabled
              className="w-full mt-2 px-4 py-3 border border-[#EDEDED] rounded-lg bg-gray-50"
              style={{ fontFamily: 'Manrope', fontSize: '16px', color: '#757575' }}
            />
          </div>
          <div>
            <label
              style={{
                fontFamily: 'Manrope',
                fontWeight: 500,
                fontSize: '14px',
                color: '#0B111E',
              }}
            >
              New Email
            </label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="w-full mt-2 px-4 py-3 border border-[#EDEDED] rounded-lg focus:outline-none focus:border-[#015FA6]"
              style={{ fontFamily: 'Manrope', fontSize: '16px' }}
              placeholder="Enter new email address"
            />
          </div>
          <div>
            <label
              style={{
                fontFamily: 'Manrope',
                fontWeight: 500,
                fontSize: '14px',
                color: '#0B111E',
              }}
            >
              Current Password
            </label>
            <p
              style={{
                fontFamily: 'Manrope',
                fontSize: '12px',
                color: '#757575',
                marginTop: '2px',
              }}
            >
              Required for security verification
            </p>
            <div className="relative mt-2">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-[#EDEDED] rounded-lg focus:outline-none focus:border-[#015FA6] pr-12"
                style={{ fontFamily: 'Manrope', fontSize: '16px' }}
                placeholder="Enter your current password"
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                    <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleSave}
              disabled={loading || !newEmail || !password}
              className="px-6 py-2 bg-[#015FA6] text-white rounded-lg hover:bg-[#014a82] transition-colors disabled:opacity-50"
              style={{ fontFamily: 'Manrope', fontWeight: 500, fontSize: '14px' }}
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={onCancel}
              className="px-6 py-2 border border-[#EDEDED] rounded-lg hover:bg-gray-50 transition-colors"
              style={{ fontFamily: 'Manrope', fontWeight: 500, fontSize: '14px', color: '#515151' }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <p style={{ fontFamily: 'Manrope', fontSize: '14px', color: '#757575' }}>Primary Contact Email</p>
            <p style={{ fontFamily: 'Manrope', fontSize: '16px', color: '#0B111E', marginTop: '4px' }}>
              {accountInfo.primaryContactEmail || '-'}
            </p>
          </div>
          <div>
            <p style={{ fontFamily: 'Manrope', fontSize: '14px', color: '#757575' }}>Account Owner</p>
            <p style={{ fontFamily: 'Manrope', fontSize: '16px', color: '#0B111E', marginTop: '4px' }}>
              {accountInfo.accountOwner || '-'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyAccountInfoCard;
